import {useEffect, useState, useCallback} from 'react';
import {getProcesses} from '../../api/processes';
import permissionsInfo from '../../constants/permissionsInfo';
import {setTitle} from '../../store/header/header.slice';
import {useAppDispatch} from '../../store/hooks';
import Error from '../common/Error';
import Spinner from '../common/Spinner';
import ProcessesElement from './ProcessesElement';
import SimpleBarReact from 'simplebar-react';
import 'simplebar/dist/simplebar.min.css';
import {ReactComponent as Arrow} from '../../assets/images/arrowsvg.svg';
import {useHistory} from 'react-router-dom';
import EnsurePermission from '../common/EnsurePermission';
import {useTranslation} from 'react-i18next';
import {DEFAULT_ERROR} from '../../api/constants';
import {getAllAlertSettings} from '../../api/settings/alert_alarm';
import {processAlertType} from '../settings/alert_alarm/constants';
import {AlertSettingsResponseProps} from '../settings/alert_alarm/types';
import routes from './routes';
import {Button} from '../common/Button';
import IconButton from '@mui/material/IconButton';

const SCHEDULING = 'SCHEDULING';

export const ProcessesList = () => {
  const {t} = useTranslation('processes');
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [isLoading, setLoading] = useState(false);
  const [processesAlertData, setProcessesAlertData] =
    useState<Nullable<AlertSettingsResponseProps>>(null);
  const [processesData, setProcessesData] = useState<any>();
  const [error, setError] = useState('');

  const getAllProcesses = useCallback(async () => {
    setLoading(true);
    try {
      const [processesAlertResponse, processDataResponse] = await Promise.all([
        getAllAlertSettings(processAlertType),
        getProcesses()]);
      setProcessesAlertData(processesAlertResponse);
      setProcessesData(processDataResponse);
    } catch (error) {
      setError(error?.message || DEFAULT_ERROR);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getAllProcesses();

    return () => {
      setError('');
    };
  }, [getAllProcesses]);

  useEffect(() => {
    dispatch(setTitle(t('pageTitle.processes')));
  }, []);

  const redirectToRunning = (key: string) => {
    history.push({
      pathname: routes.running(),
      search: `?type=${key}`
    });
  };

  const redirectToStart = () => {
    history.push(routes.start());
  };

  if (error) {
    return (<Error message={error} />);
  }

  return (
    <div className="container">
      {isLoading || !processesData ?
        <Spinner loadingText={t('loadingText.processes')}/> :
        <div className="processes-wrapper">
          {Object.keys(processesData).map((key) => (
            <div
              key={`processes-card_${key}`}
              className="processes-card">
              <div className="processes-card_title-wrapper">
                <h2 className="processes-card_title">
                  {t(`processNames.${key}`)}
                  <IconButton
                    className="icon-button blue"
                    onClick={() => {
                      redirectToRunning(key);
                    }}
                  >
                    <div className="icon processes-card_redirect">
                      <Arrow/><Arrow/>
                    </div>
                  </IconButton>
                </h2>
                {key === SCHEDULING &&
                    <EnsurePermission
                      elementPermissions={permissionsInfo.processes.start}
                    >
                      <Button
                        variant="text"
                        size="small"
                        onClick={redirectToStart}
                        label={`+ ${t('processCardHeader.processStart')}`}
                      />
                    </EnsurePermission>
                }
              </div>
              <div className="processes-elements">
                <p className="processes-count">{processesData[key].length}</p>
                <SimpleBarReact
                  className="processes-elements_wrapper"
                  autoHide={false}>
                  {processesData[key]
                      .map(({
                        id, targetTime, startedAt, completedAt,
                        patient, currentTask, type: {code: processCode}
                      }) => (
                        <ProcessesElement
                          key={`process-element_${id}`}
                          id={id}
                          targetTime={targetTime}
                          startedAt={startedAt}
                          patient={patient}
                          currentTask={currentTask}
                          processesAlertData={processesAlertData}
                          completedAt={completedAt}
                          processCode={processCode}
                        />
                      ))}
                </SimpleBarReact>
              </div>
            </div>
          ))}
        </div>}
    </div>
  );
};

export default ProcessesList;
